import React from 'react'
import PublicLayout from '@/pages/PublicLayout';
import NavigationBar from '@/components/pages/Landing/components/NavigationBar';
import { MODE_DARK, MODE_LIGHT } from '@/constants/theme.constant';
import useDarkMode from '@/utils/hooks/useDarkMode';
import LandingContainer from '@/components/pages/Landing/components/LandingContainer';
import { Head } from '@inertiajs/react';

const Terms = () => {
  const [isDark, setMode] = useDarkMode()

  const mode = isDark ? MODE_DARK : MODE_LIGHT

  const toggleMode = () => {
    setMode(mode === MODE_LIGHT ? MODE_DARK : MODE_LIGHT)
  }

  return (
    <main className="sm:px-4 lg:px-0 text-base">
      <Head>
        <title>Terms and Conditions</title>
        <meta name="description" content="Read the Terms and Conditions for using MemoSlate, an early-access note-taking application by Themety. Understand your rights and responsibilities while using our service." />
      </Head>

      <NavigationBar toggleMode={toggleMode} mode={mode} />
      <LandingContainer className="relative mt-36">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12 lg:p-16">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Terms and Conditions
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
            Last Updated: July 3, 2026
          </p>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                1. Agreement to Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                These Terms and Conditions ("Terms") constitute a legally binding agreement between you and
                Themety ("Company", "we", "us", or "our"), operating MemoSlate, concerning your
                access to and use of the memoslate.com website and the MemoSlate application (collectively, the "Service").
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                By accessing or using the Service, you agree to be bound by these Terms. If you do not agree
                to these Terms, you may not access or use the Service.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You must be at least 18 years old to use this Service. By using the Service, you represent
                and warrant that you are at least 18 years of age.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                2. Description of Service
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                MemoSlate is an early-access personal notes workspace that allows you to create, edit,
                organize, and manage private notes, checklists, and sensitive snippets. The Service may
                include features such as:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>creating and editing notes with text and formatting</li>
                <li>organizing and managing personal notes</li>
                <li>using checklists inside notes</li>
                <li>storing certain sensitive snippets in secret fields</li>
                <li>accessing notes from supported devices with internet connectivity</li>
                <li>storing notes and related data on our servers</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                The Service is under active development. Some features may be incomplete, experimental,
                changed, limited, or removed over time.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time,
                with or without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                3. Early Access Status
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                MemoSlate is currently provided as an early-access product and is under active development.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>some features may be incomplete, experimental, or subject to change;</li>
                <li>the Service may contain bugs, interruptions, or limitations;</li>
                <li>features may be added, changed, limited, or removed over time;</li>
                <li>the Service should not be used as the only storage location for critical information.</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                We may apply reasonable limits to accounts, storage, note history, usage, or other features
                to maintain the stability, security, and sustainability of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                4. User Accounts and Registration
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To use certain features of the Service, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password and account credentials</li>
                <li>Accept all responsibility for activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You may delete your account at any time through your user panel, if this feature is
                available, or by contacting us.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                After account deletion, we will delete or anonymize your account data and User Content
                within a reasonable period, except where retention is required or permitted by law, security,
                fraud prevention, backups, or legitimate operational needs.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Residual copies may remain in backups, logs, or technical records for a limited period before
                being overwritten or deleted according to our retention practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                5. User Content and Data
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You retain all ownership rights to the content you create and store using the Service ("User Content").
                By using the Service, you grant us a limited license to store, process, and display your User Content
                solely for the purpose of providing the Service to you.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You are solely responsible for:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>All User Content you create, upload, or store using the Service</li>
                <li>Ensuring your User Content does not violate any applicable laws or third-party rights</li>
                <li>Maintaining backup copies of your important data</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Your notes and related data are stored on our servers located in Germany. Certain features,
                such as secret fields, may use client-side encryption so that sensitive values are encrypted
                before they are stored.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Not all User Content is end-to-end encrypted. Regular note content, metadata, account data,
                and technical information may be processed by us as necessary to provide, secure, maintain,
                and improve the Service. We do not share your data with third parties except as required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                6. Acceptable Use
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others, including intellectual property rights</li>
                <li>Transmit any viruses, malware, or other malicious code</li>
                <li>Attempt to gain unauthorized access to the Service or related systems</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use automated means to access the Service without our express written permission</li>
                <li>Impersonate any person or entity</li>
                <li>Harass, abuse, or harm another person</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                We reserve the right to investigate and take appropriate action against anyone who violates
                these Terms, including removing content and terminating accounts.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                7. Fees and Payment
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                The Service is currently provided free of charge. We reserve the right to introduce paid
                features or subscription plans in the future. If we introduce paid features:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>We will provide advance notice of any fees</li>
                <li>Existing users will have the option to continue using free features or upgrade to paid plans</li>
                <li>Payment terms and conditions will be clearly disclosed before purchase</li>
                <li>All fees are non-refundable unless otherwise stated</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may apply limits to free accounts, including limits on storage, number of notes, note
                history, file size, usage frequency, or other Service features. These limits may change
                over time.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                If paid plans are introduced in the future, we may offer higher or different limits as part
                of those plans.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                8. Advertising
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                We may display advertisements within the Service in the future. We reserve the right to
                introduce advertising at our discretion, with advance notice to users.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                9. Intellectual Property Rights
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                The Service, including its design, functionality, text, graphics, logos, and software, is
                owned by the Company and protected by international copyright, trademark, and other intellectual
                property laws.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You may not copy, modify, distribute, sell, or lease any part of our Service without our
                express written permission. You may not reverse engineer or attempt to extract the source
                code of our software.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                10. Termination
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>By You:</strong> You may terminate your account at any time by using the account
                deletion feature in your user panel, if available, or by contacting us. Upon termination,
                your access to the Service will cease, and your data will be deleted or anonymized as
                described in the "User Accounts and Registration" section above.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>By Us:</strong> We reserve the right to suspend or terminate your account and access
                to the Service at any time, with or without notice, for any reason, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Violation of these Terms</li>
                <li>Fraudulent, abusive, or illegal activity</li>
                <li>Extended periods of inactivity</li>
                <li>Request by law enforcement or government agencies</li>
                <li>Discontinuation of the Service</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                Upon termination by either party, your right to use the Service will immediately cease.
                We are not liable for any loss or damage resulting from account termination.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                11. Disclaimers and Limitation of Liability
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4 font-semibold uppercase">
                Important Legal Protections:
              </p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 mb-4">
                <p className="text-gray-800 dark:text-gray-200 mb-4">
                  <strong>NO WARRANTIES:</strong> THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
                  WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
                <p className="text-gray-800 dark:text-gray-200 mb-4">
                  WE DO NOT WARRANT THAT:
                </p>
                <ul className="list-disc list-inside text-gray-800 dark:text-gray-200 space-y-1">
                  <li>The Service will be uninterrupted, secure, or error-free</li>
                  <li>The results obtained from the Service will be accurate or reliable</li>
                  <li>Any errors in the Service will be corrected</li>
                  <li>Your data will be backed up or preserved without loss</li>
                </ul>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Data Loss Disclaimer:</strong> While we implement reasonable security measures, we cannot
                guarantee absolute security of your data. You acknowledge that you use the Service at your own risk
                and are solely responsible for maintaining backup copies of important information.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Because the Service is in early access, you should maintain independent backups of important
                information. The Service should not be treated as the only storage location for critical
                business, legal, financial, medical, security-sensitive, or otherwise important information.
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mb-4">
                <p className="text-gray-800 dark:text-gray-200 mb-4">
                  <strong>LIMITATION OF LIABILITY:</strong> TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
                  IN NO EVENT SHALL THE COMPANY, ITS DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT,
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF
                  PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </p>
                <ul className="list-disc list-inside text-gray-800 dark:text-gray-200 space-y-1">
                  <li>Your access to or use of or inability to access or use the Service</li>
                  <li>Any conduct or content of any third party on the Service</li>
                  <li>Unauthorized access, use, or alteration of your content</li>
                  <li>Any interruption or cessation of the Service</li>
                  <li>Any bugs, viruses, or the like that may be transmitted to or through the Service</li>
                  <li>Any errors or omissions in any content or for any loss or damage incurred as a result</li>
                </ul>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                THE COMPANY'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICE
                SHALL NOT EXCEED THE AMOUNT YOU PAID TO US FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE
                CLAIM, OR €50, WHICHEVER IS GREATER.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                12. Indemnification
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                You agree to indemnify, defend, and hold harmless the Company, its affiliates, and their
                respective officers, directors, employees, and agents from and against any claims, liabilities,
                damages, losses, and expenses, including reasonable legal fees, arising out of or in any way
                connected with: (a) your access to or use of the Service; (b) your User Content; (c) your
                violation of these Terms; or (d) your violation of any rights of another party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                13. Governing Law and Jurisdiction
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the Republic of
                Lithuania, without regard to its conflict of law provisions.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Any disputes arising from these Terms or your use of the Service shall be subject to the
                exclusive jurisdiction of the courts of Vilnius, Lithuania.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                14. Changes to Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We reserve the right to modify these Terms at any time. If we make material changes, we will
                notify you by:
              </p>
              <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                <li>Posting a notice on the Service</li>
                <li>Updating the "Last Updated" date at the top of these Terms</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                Your continued use of the Service after any changes indicates your acceptance of the new Terms.
                If you do not agree to the modified Terms, you must stop using the Service and delete your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                15. Privacy and Data Protection
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Your privacy is important to us. Our collection and use of personal information is described
                in our Privacy Policy. By using the Service, you consent to our collection and use of personal
                data as outlined in the Privacy Policy.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                We comply with applicable data protection laws, including the General Data Protection Regulation
                (GDPR) where applicable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                16. General Provisions
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and
                the Company regarding the Service and supersede all prior agreements.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>Severability:</strong> If any provision of these Terms is found to be invalid or
                unenforceable, the remaining provisions will remain in full force and effect.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>No Waiver:</strong> Our failure to enforce any right or provision of these Terms will
                not be considered a waiver of those rights.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Assignment:</strong> You may not assign or transfer these Terms without our prior written
                consent. We may assign these Terms without restriction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                17. Contact Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-gray-800 dark:text-gray-200 mb-2">
                  <strong>Email:</strong> admin@memoslate.com
                </p>
                <p className="text-gray-800 dark:text-gray-200">
                  <strong>Location:</strong> Vilnius, Lithuania
                </p>
              </div>
            </section>

            <div className="border-t border-gray-300 dark:border-gray-700 pt-8 mt-12">
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                By using MemoSlate, you acknowledge that you have read, understood, and agree to be bound
                by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>
      </LandingContainer>
    </main>
  );
}

Terms.layout = (page: React.ReactNode) => <PublicLayout children={page} />;
export default Terms;

